import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@workspace/public-utils'
import toast from 'react-hot-toast';
import { Seo } from '../../components/Seo';

interface CurrentSubscription {
  subscription_tier: string;
  subscription_status: string;
  subscription_start_date: string;
  subscription_end_date: string;
}

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const seo = <Seo title="구독 관리" canonicalPath="/dashboard/subscription" noindex />;
  const [loading, setLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);

  useEffect(() => {
    fetchCurrentSubscription();
  }, []);

  const fetchCurrentSubscription = async () => {
    try {
      setLoading(true);
      // Fetch current user profile with subscription info
      const profileResponse = await apiClient.get('/api/v1/users/profile/me');
      
      if (profileResponse.data.subscription_tier) {
        setCurrentSubscription({
          subscription_tier: profileResponse.data.subscription_tier,
          subscription_status: profileResponse.data.subscription_status || 'active',
          subscription_start_date: profileResponse.data.subscription_start_date,
          subscription_end_date: profileResponse.data.subscription_end_date,
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch subscription data:', err);
      toast.error(err.response?.data?.detail || '구독 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        {seo}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {seo}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">구독 관리</h1>
        
        {/* Current Subscription */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">현재 구독</h2>
          
          {currentSubscription ? (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{currentSubscription.subscription_tier}</h3>
                  <p className={`text-sm font-medium ${
                    currentSubscription.subscription_status === 'active' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {currentSubscription.subscription_status === 'active' ? '✓ 활성' : 
                     currentSubscription.subscription_status === 'expired' ? '만료됨' : 
                     currentSubscription.subscription_status === 'cancelled' ? '취소됨' : '비활성'}
                  </p>
                </div>
                <button 
                  onClick={() => navigate('/plans')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  플랜 변경
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                {currentSubscription.subscription_start_date && (
                  <div>
                    <p className="text-sm text-gray-600">시작일</p>
                    <p className="text-base font-semibold text-gray-900">
                      {new Date(currentSubscription.subscription_start_date).toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                )}
                
                {currentSubscription.subscription_end_date && (
                  <div>
                    <p className="text-sm text-gray-600">종료일</p>
                    <p className="text-base font-semibold text-gray-900">
                      {new Date(currentSubscription.subscription_end_date).toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">현재 구독 중인 플랜이 없습니다</p>
              <button
                onClick={() => navigate('/plans')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                플랜 구독하기
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SubscriptionPage;
